// components/ImageUploadModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Group, Button, Text, Image } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { IconUpload, IconPhoto, IconX } from '@tabler/icons-react';
import ImageCropper from './ImageCropper';
import '@mantine/dropzone/styles.css';

interface ImageUploadModalProps {
  previewUrl?: string | null;
  onImageChange: (file: File) => void;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({ previewUrl, onImageChange }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  // Common handler for any image drop/selection
  const handleDrop = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      console.log('📸 Image selected:', file);

      // Set image source for cropping and open the modal
      setImageSrc(URL.createObjectURL(file));
      setModalOpen(true);
    }
  };

  // Called by the cropper when an image has been cropped
  const handleCropped = (blob: Blob) => {
    setCroppedBlob(blob);
  };

  const handleModalSubmit = () => {
    if (croppedBlob) {
      const newFile = new File([croppedBlob], 'cropped_image.jpg', { type: croppedBlob.type });
      onImageChange(newFile);
      setModalOpen(false);
      setImageSrc(null);
      setCroppedBlob(null);
    }
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    setImageSrc(null);
    setCroppedBlob(null);
  };

  return (
    <div>
      {previewUrl ? (
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1rem' }}>
          <Image src={previewUrl} alt="Image preview" width={300} height="auto" radius="md" />
          <div
            onClick={() => setModalOpen(true)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              background: isHovering ? 'rgba(245, 208, 117, 0.7)' : 'rgba(128, 59, 10, 0.5)',
              width: 75,
              height: 75,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.3s ease',
            }}
          >
            {isHovering && (
              <Text
                c="white"
                size="md"
                style={{
                  position: 'absolute',
                  top: -40,
                  whiteSpace: 'nowrap',
                  background: 'rgba(0, 0, 0, 0.7)',
                  padding: '1rem 2rem',
                  borderRadius: 4,
                  fontSize: 16,
                }}
              ></Text>
            )}
            <IconPhoto size={48} color="white" />
          </div>
        </div>
      ) : (
        // If no preview exists, display an inline dropzone.
        <div style={{ marginBottom: '1rem' }}>
          <Dropzone
            onDrop={handleDrop} // Use the same handler as the modal dropzone
            onReject={(files) => console.log('Rejected files:', files)}
            maxSize={5 * 1024 ** 2}
            accept={IMAGE_MIME_TYPE}
            styles={() => ({
              root: {
                backgroundColor: '#ea580b20',
                border: '3px dashed #f6b73c',
              },
            })}
          >
            <Group justify="center" gap="md" mih={100} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload size={52} color="var(--mantine-color-blue-2)" stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={52} color="var(--mantine-color-orange-3)" stroke={1.5} />
              </Dropzone.Idle>
              <div>
                <Text size="xl" inline className="text-white mb-1">
                  Drag image here or click to select file
                </Text>
                <Text size="sm" c="yellow" inline mt={7}>
                  Image must be under 5 MB
                </Text>
              </div>
            </Group>
          </Dropzone>
        </div>
      )}

      <Modal opened={modalOpen} onClose={handleModalCancel} title="Crop Image" size="lg">
        {/* Show dropzone if no image has been dropped yet */}
        {!imageSrc ? (
          <Dropzone
            onDrop={handleDrop}
            onReject={(files) => console.log('Rejected files:', files)}
            maxSize={5 * 1024 ** 2}
            accept={IMAGE_MIME_TYPE}
            mb="md"
            styles={() => ({
              root: {
                backgroundColor: '#fcefe1',
                border: '3px dashed #f6b73c',
              },
            })}
          >
            <Group justify="center" gap="md" mih={100} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconUpload size={52} color="var(--mantine-color-blue-2)" stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={52} color="var(--mantine-color-red-6)" stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconPhoto size={52} color="var(--mantine-color-orange-3)" stroke={1.5} />
              </Dropzone.Idle>
              <div>
                <Text size="xl" inline>
                  Drag image here or click to select file
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  Image must be under 5 MB
                </Text>
              </div>
            </Group>
          </Dropzone>
        ) : (
          // Show cropper once an image is selected
          <ImageCropper src={imageSrc} aspectRatio={16 / 9} setCroppedBlob={handleCropped} />
        )}

        <Group justify="right" mt="md">
          <Button variant="outline" onClick={handleModalCancel}>
            Cancel
          </Button>
          <Button onClick={handleModalSubmit} disabled={!croppedBlob}>
            Submit
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default ImageUploadModal;
